import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, CheckCircle2, Download, Mail } from "lucide-react";
import api, { formatApiError } from "../api";
import {
  emptySituation,
  StepSituation,
  StepArbre,
  StepDocuments,
  SectionHeader,
  RoadmapUnlocked,
} from "./Succession";
import { LogoLight } from "../components/Logo";

/*
 * SUCCESSION PUBLIC WIZARD — /ouvrir-une-succession/diagnostic
 * Parcours 100 % public sans compte, aligné sur le modèle /bilan-succession :
 *  - 3 étapes de saisie (situation, arbre, documents)
 *  - 1 étape "coordonnées" en fin
 *  - Résultat + paywall accessibles via un lien magique par token
 */

const PUB_STEPS = [
  { key: 0, label: "Ma situation" },
  { key: 1, label: "Arbre généalogique" },
  { key: 2, label: "Mes documents" },
  { key: 3, label: "Mes coordonnées" },
];

export function SuccessionPublicWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [situation, setSituation] = useState(emptySituation());
  const [arbre, setArbre] = useState([]);
  const [documents, setDocuments] = useState({});
  const [coords, setCoords] = useState({ prenom: "", nom: "", email: "", telephone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (typeof document !== "undefined") document.title = "Diagnostic succession · AUPRÉA";
  }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/succession/config");
        setConfig(data);
      } catch (_e) {/* ignore, wizard fonctionne sans */}
    })();
  }, []);

  const canGoNext = useMemo(() => {
    if (step === 3) {
      const emailOk = /\S+@\S+\.\S+/.test(coords.email);
      return coords.prenom.trim() && coords.nom.trim() && emailOk;
    }
    return true; // étapes 0-2 : toutes les questions sont optionnelles (comme /bilan-succession)
  }, [step, coords]);

  const goNext = async () => {
    if (!canGoNext) return;
    if (step < 3) { setStep(step + 1); return; }
    // step 3 → submit
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post("/public/succession/submit-diagnostic", {
        prenom: coords.prenom.trim(),
        nom: coords.nom.trim(),
        email: coords.email.trim().toLowerCase(),
        telephone: coords.telephone.trim(),
        situation, arbre, documents_checklist: documents,
      });
      try { localStorage.setItem("succ_public_token", data.public_token); } catch (_e) {}
      nav(`/ouvrir-une-succession/resultat?token=${encodeURIComponent(data.public_token)}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="succession-theme" style={{ minHeight: "100vh" }} data-testid="public-succession-wizard">
      <PubHeader />
      <ProgressBar step={step} />
      <main style={{ maxWidth: 900, margin: "0 auto", padding: "32px 20px 60px" }}>
        {err && (
          <div style={{ background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.3)", color: "#b53a3a", padding: "12px 16px", borderRadius: 4, marginBottom: 20, fontSize: 14 }} data-testid="pub-succ-error">
            {err}
          </div>
        )}
        {step === 0 && <StepSituation situation={situation} setSituation={setSituation} config={config} />}
        {step === 1 && <StepArbre arbre={arbre} setArbre={setArbre} config={config} />}
        {step === 2 && <StepDocuments documents={documents} setDocuments={setDocuments} config={config} />}
        {step === 3 && <StepCoords coords={coords} setCoords={setCoords} />}

        <div className="flex items-center justify-between mt-10 gap-3">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="succ-btn-outline" data-testid="pub-btn-prev">
              <ChevronLeft size={16} style={{ marginRight: 6, display: "inline" }} /> Précédent
            </button>
          ) : <span />}
          <button
            onClick={goNext}
            disabled={!canGoNext || submitting}
            className="succ-btn-primary"
            data-testid={`pub-btn-next-${step}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, opacity: (!canGoNext || submitting) ? 0.6 : 1 }}
          >
            {submitting ? "Envoi…" : (step === 3 ? "Voir mon diagnostic gratuit →" : "Étape suivante →")}
          </button>
        </div>
      </main>
      <PubFooter />
    </div>
  );
}

function StepCoords({ coords, setCoords }) {
  const set = (k, v) => setCoords((c) => ({ ...c, [k]: v }));
  return (
    <div data-testid="step-coords">
      <SectionHeader n={4} title="Mes coordonnées" subtitle="Nous vous envoyons votre diagnostic par e-mail et vous pourrez y revenir à tout moment via un lien sécurisé." />
      <div className="succ-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="succ-label">Prénom</label>
            <input className="succ-input" value={coords.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="pub-input-prenom" />
          </div>
          <div>
            <label className="succ-label">Nom</label>
            <input className="succ-input" value={coords.nom} onChange={(e) => set("nom", e.target.value)} data-testid="pub-input-nom" />
          </div>
          <div>
            <label className="succ-label">Email</label>
            <input type="email" className="succ-input" value={coords.email} onChange={(e) => set("email", e.target.value)} data-testid="pub-input-email" />
          </div>
          <div>
            <label className="succ-label">Téléphone (facultatif)</label>
            <input className="succ-input" value={coords.telephone} onChange={(e) => set("telephone", e.target.value)} data-testid="pub-input-tel" />
          </div>
        </div>
        <div style={{ marginTop: 18, fontSize: 12, color: "var(--succ-muted)", lineHeight: 1.6 }}>
          Vos données sont utilisées uniquement pour vous transmettre votre diagnostic et, si vous le souhaitez ensuite,
          la feuille de route complète (89&nbsp;€, paiement unique). Aucun mot de passe n'est requis à ce stade —
          vous accéderez à votre dossier via un lien sécurisé envoyé par e-mail.
        </div>
      </div>
    </div>
  );
}

/* =========================================================================== */
/* SuccessionPublicResult — /ouvrir-une-succession/resultat?token=xxx           */
/* =========================================================================== */

export function SuccessionPublicResult() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pollingPayment, setPollingPayment] = useState(false);
  const [resendStatus, setResendStatus] = useState("");

  useEffect(() => {
    if (typeof document !== "undefined") document.title = "Mon diagnostic succession · AUPRÉA";
  }, []);

  const loadDoc = useCallback(async () => {
    if (!token) { setErr("Lien invalide."); setLoading(false); return; }
    try {
      const { data } = await api.get(`/public/succession/${encodeURIComponent(token)}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e) || "Dossier introuvable.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDoc(); }, [loadDoc]);

  const pollPaid = useCallback(async () => {
    setPollingPayment(true);
    try {
      for (let i = 0; i < 20; i++) {
        const { data } = await api.get(`/public/succession/${encodeURIComponent(token)}`);
        if (data.paid) {
          setDoc(data);
          setResendStatus("Paiement confirmé — feuille de route débloquée !");
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
      setResendStatus("Paiement en cours de traitement. Rechargez la page dans quelques minutes si votre feuille de route n'apparaît pas.");
    } finally {
      setPollingPayment(false);
    }
  }, [token]);

  const openPayment = async () => {
    try {
      const { data } = await api.get(`/public/succession/${encodeURIComponent(token)}/payment-link`);
      window.open(data.url, "_blank", "noopener,noreferrer");
      pollPaid();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const resendPDF = async () => {
    setResendStatus("Envoi en cours…");
    try {
      await api.post(`/public/succession/${encodeURIComponent(token)}/resend-pdf`);
      setResendStatus(`Feuille de route renvoyée à ${doc?.email || "vous"}`);
    } catch (e) {
      setResendStatus("Erreur : " + formatApiError(e));
    }
  };

  const downloadPDF = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/succession/${encodeURIComponent(token)}/pdf`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="succession-theme" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "var(--succ-muted)" }}>Chargement de votre diagnostic…</div>
      </div>
    );
  }
  if (err || !doc) {
    return (
      <div className="succession-theme" style={{ minHeight: "100vh" }}>
        <PubHeader />
        <main style={{ maxWidth: 700, margin: "0 auto", padding: "60px 20px" }}>
          <div className="succ-card" data-testid="pub-succ-result-error">
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Lien introuvable</h1>
            <p style={{ color: "var(--succ-muted)" }}>{err || "Ce lien magique n'est plus valide."}</p>
            <Link to="/ouvrir-une-succession/diagnostic" className="succ-btn-primary" style={{ display: "inline-block", marginTop: 12 }}>
              Refaire mon diagnostic →
            </Link>
          </div>
        </main>
        <PubFooter />
      </div>
    );
  }

  const paid = !!doc.paid;
  const diag = doc.diagnostic || {};
  const roadmap = doc.roadmap || null;
  const price = diag.price_eur || 89;
  const defunt = `${doc.situation?.defunt_prenom || ""} ${doc.situation?.defunt_nom || ""}`.trim() || "—";

  return (
    <div className="succession-theme" style={{ minHeight: "100vh" }} data-testid="pub-succ-result-page">
      <PubHeader />
      <main style={{ maxWidth: 900, margin: "0 auto", padding: "32px 20px 60px" }}>
        <div style={{ background: "rgba(31,94,86,0.06)", padding: "14px 18px", borderRadius: 4, marginBottom: 20, fontSize: 13, color: "var(--succ-muted)" }}>
          Bonjour <strong style={{ color: "var(--succ-teal-dark)" }}>{doc.prenom}</strong>, votre diagnostic est prêt.
          Ce lien reste accessible à tout moment, un e-mail vous a été envoyé à {doc.email}.
        </div>

        {/* Bloc gratuit — diagnostic */}
        <div className="succ-card mb-6" data-testid="pub-diagnostic-libre">
          <div style={{ color: "var(--succ-brass-dark)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Diagnostic général — gratuit</div>
          <h2 style={{ margin: "0 0 12px 0", fontSize: 24 }}>Succession de {defunt}</h2>
          <p style={{ color: "var(--succ-ink)", lineHeight: 1.6, fontSize: 15, marginBottom: 16 }}>{diag.resume}</p>
          {(diag.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {diag.tags.map((t) => {
                const meta = (diag.tag_meta || {})[t] || {};
                return (
                  <span key={t} style={{ background: meta.color || "var(--succ-teal)", color: "#fff", padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: "0.03em" }} data-testid={`pub-tag-${t}`}>
                    {meta.label || t}
                  </span>
                );
              })}
            </div>
          )}
          {(diag.tags || []).length > 0 && diag.tag_meta && (
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 14, color: "var(--succ-muted)", lineHeight: 1.65 }}>
              {diag.tags.map((t) => {
                const meta = (diag.tag_meta || {})[t] || {};
                return <li key={t}><strong style={{ color: "var(--succ-teal-dark)" }}>{meta.label}</strong> — {meta.message}</li>;
              })}
            </ul>
          )}
          {(diag.professions_recommended || []).length > 0 && (
            <div className="mt-6">
              <div className="succ-label mb-2">Professionnels potentiellement concernés</div>
              <div className="flex flex-wrap gap-2">
                {diag.professions_recommended.map((p) => {
                  const m = (diag.professions_meta || {})[p] || {};
                  return (
                    <span key={p} style={{ background: "rgba(31,94,86,0.08)", color: "var(--succ-teal-dark)", padding: "6px 12px", borderRadius: 4, fontSize: 13, border: "1px solid rgba(31,94,86,0.2)" }}>
                      {m.icon} {m.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {paid && roadmap ? (
          <RoadmapUnlocked roadmap={roadmap} downloadPDF={downloadPDF} resendPDF={resendPDF} resendStatus={resendStatus} />
        ) : (
          <PubPaywallCard
            price={price}
            openPayment={openPayment}
            pollingPayment={pollingPayment}
            onRefreshPaid={pollPaid}
            token={token}
            onPromoUnlocked={pollPaid}
          />
        )}

        <div style={{ background: "rgba(31,94,86,0.05)", border: "1px solid rgba(31,94,86,0.15)", padding: "14px 18px", borderRadius: 4, marginTop: 24, fontSize: 12, color: "var(--succ-muted)", lineHeight: 1.55 }}>
          AUPRÉA n'est ni notaire, ni avocat, ni géomètre. Ce diagnostic ne remplace pas une consultation professionnelle : il vous aide à savoir par où commencer et à préparer vos rendez-vous.
        </div>
      </main>
      <PubFooter />
    </div>
  );
}

function PubPaywallCard({ price, openPayment, pollingPayment, onRefreshPaid, token, onPromoUnlocked }) {
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState("");
  const [promoMsg, setPromoMsg] = useState("");

  const submitPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoStatus("loading"); setPromoMsg("");
    try {
      await api.post(`/public/succession/${encodeURIComponent(token)}/apply-promo`, { code: promoCode.trim() });
      setPromoStatus("success");
      setPromoMsg("Code accepté — feuille de route en cours de génération…");
      if (typeof onPromoUnlocked === "function") await onPromoUnlocked();
    } catch (e) {
      setPromoStatus("error");
      setPromoMsg(formatApiError(e) || "Code invalide.");
    }
  };

  return (
    <div className="succ-card" style={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #fff 0%, rgba(31,94,86,0.04) 100%)" }} data-testid="pub-paywall-card">
      <div style={{ position: "absolute", top: 12, right: 12, background: "var(--succ-brass)", color: "#fff", padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>{price} €</div>
      <div style={{ color: "var(--succ-brass-dark)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Feuille de route complète</div>
      <h2 style={{ margin: "0 0 14px 0", fontSize: 22 }}>
        <Lock size={20} style={{ display: "inline", marginRight: 8, verticalAlign: "middle", color: "var(--succ-brass)" }} />
        Débloquez votre feuille de route personnalisée
      </h2>
      <p style={{ fontSize: 15, lineHeight: 1.65, color: "var(--succ-ink)", marginBottom: 16 }}>
        Pour <strong>{price} €</strong>, paiement unique, obtenez :
      </p>
      <ul style={{ paddingLeft: 20, marginBottom: 20, color: "var(--succ-ink)", fontSize: 14, lineHeight: 1.7 }}>
        <li>L'ordre précis des démarches (professionnel par professionnel)</li>
        <li>Le raisonnement personnalisé pour chaque recommandation</li>
        <li>La liste des documents à réunir pour chaque rendez-vous</li>
        <li>Les actions concrètes à préparer avant chaque consultation</li>
        <li>La feuille de route <strong>envoyée par e-mail au format PDF</strong></li>
      </ul>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={openPayment} className="succ-btn-primary" style={{ padding: "14px 30px", fontSize: 15 }} data-testid="pub-btn-pay-stripe">
          Payer {price} € et débloquer →
        </button>
        {pollingPayment && (
          <span style={{ fontSize: 13, color: "var(--succ-teal)" }} data-testid="pub-payment-polling">
            En attente de la confirmation Stripe…
          </span>
        )}
        {!pollingPayment && (
          <button onClick={onRefreshPaid} className="succ-btn-outline" data-testid="pub-btn-check-payment" style={{ fontSize: 13, padding: "8px 14px" }}>
            J'ai payé — vérifier maintenant
          </button>
        )}
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: "var(--succ-muted)" }}>
        Paiement sécurisé via Stripe · Vous serez redirigé(e) vers une page Stripe officielle.
      </div>

      {/* Code promo */}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid rgba(31,94,86,0.1)" }}>
        {!showPromo ? (
          <button type="button" onClick={() => setShowPromo(true)} data-testid="pub-btn-toggle-promo"
            style={{ background: "transparent", border: "none", color: "var(--succ-muted)", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}>
            J'ai un code promo
          </button>
        ) : (
          <div>
            <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--succ-muted)", marginBottom: 6, fontWeight: 600 }}>Code promo</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input type="text" value={promoCode} onChange={(e) => { setPromoCode(e.target.value); if (promoStatus === "error") { setPromoStatus(""); setPromoMsg(""); } }}
                placeholder="Saisir votre code" autoComplete="off" data-testid="pub-input-promo-code"
                disabled={promoStatus === "loading" || promoStatus === "success"}
                onKeyDown={(e) => { if (e.key === "Enter") submitPromo(); }}
                style={{ minWidth: 220, padding: "10px 12px", fontSize: 14, border: "1px solid var(--succ-line)", borderRadius: 4, background: "#fff", color: "var(--succ-ink)" }} />
              <button type="button" onClick={submitPromo}
                disabled={promoStatus === "loading" || promoStatus === "success" || !promoCode.trim()}
                className="succ-btn-outline" data-testid="pub-btn-apply-promo"
                style={{ fontSize: 13, padding: "10px 18px", opacity: (promoStatus === "loading" || promoStatus === "success" || !promoCode.trim()) ? 0.6 : 1 }}>
                {promoStatus === "loading" ? "Vérification…" : promoStatus === "success" ? "Validé ✓" : "Valider"}
              </button>
              {promoStatus !== "success" && (
                <button type="button" onClick={() => { setShowPromo(false); setPromoCode(""); setPromoStatus(""); setPromoMsg(""); }}
                  style={{ background: "transparent", border: "none", color: "var(--succ-muted)", fontSize: 12, cursor: "pointer", padding: "10px 4px", textDecoration: "underline", textUnderlineOffset: 3 }}>
                  Annuler
                </button>
              )}
            </div>
            {promoMsg && (
              <div data-testid={`pub-promo-msg-${promoStatus}`}
                style={{ marginTop: 8, fontSize: 13, color: promoStatus === "error" ? "#b53a3a" : promoStatus === "success" ? "var(--succ-teal)" : "var(--succ-muted)" }}>
                {promoMsg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Header/Footer & ProgressBar (charte teal) */
function PubHeader() {
  return (
    <header style={{ background: "var(--succ-teal-dark)", padding: "16px 24px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", display: "inline-flex" }}>
          <LogoLight size={26} />
        </Link>
        <div style={{ fontSize: 12, color: "rgba(239,241,236,0.7)", fontStyle: "italic" }}>
          Anticiper est un acte d'amour.
        </div>
      </div>
    </header>
  );
}

function PubFooter() {
  return (
    <footer style={{ background: "var(--succ-teal-dark)", color: "rgba(239,241,236,0.7)", padding: "18px 24px", textAlign: "center", fontSize: 12 }}>
      <em>« Anticiper est un acte d'amour. »</em> — AUPRÉA SAS · Grand-Bourg, Guadeloupe · contact@auprea.com
    </footer>
  );
}

function ProgressBar({ step }) {
  const pct = Math.round(((step + 1) / PUB_STEPS.length) * 100);
  return (
    <div style={{ background: "#fff", borderBottom: "1px solid rgba(31,94,86,0.1)", padding: "16px 20px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }} data-testid="pub-succ-progress">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "var(--succ-brass-dark)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
            Étape {step + 1} sur {PUB_STEPS.length} — {PUB_STEPS[step]?.label}
          </div>
          <div style={{ fontSize: 12, color: "var(--succ-muted)" }}>{pct}%</div>
        </div>
        <div style={{ background: "rgba(31,94,86,0.12)", height: 6, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ background: "var(--succ-brass)", height: "100%", width: `${pct}%`, borderRadius: 3, transition: "width 0.35s ease" }} />
        </div>
      </div>
    </div>
  );
}

export default SuccessionPublicWizard;
